<template>
  <v-card>
    <v-toolbar color="indigo" dark dense>
      <v-toolbar-title>
        <v-icon left>{{ barIcon }}</v-icon>
        {{ $vuetify.lang.t('$vuetify.map.tripScroll.title') }}
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="showContent = !showContent">
        <v-icon>{{ showContent ? upIcon : downIcon }}</v-icon>
      </v-btn>
    </v-toolbar>
    <v-expand-transition>
      <div class="tripScroll my-1" v-if="showContent">

        <v-data-table v-if="driverData"
                      :headers="headers"
                      :items="driverData.trips"
                      :items-per-page="5"
                      :mobile-breakpoint="0"
                      item-key="date_start"
                      :options.sync="driverData.options"
                      :loading="tableLoading"
                      :server-items-length="driverData.totalTrips"
                      multi-sort
                      :footer-props="footerProps"
                      dense
                      class="elevation-1">

          <!-- header slots -->
          <template v-for="item in iconedHeaders" v-slot:[`header.${item}`]="{header}">
        <span :key="item">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon left class="pointer" v-on="on">{{ header.icon }}</v-icon>
            </template>
            <span v-html="header.text"></span>
          </v-tooltip>
        </span>
          </template>

          <template v-slot:item.date_start="{item}">
            {{ travelDateLocalized(item.date_start) }}
            <v-icon left v-if="travelStart === item.date_start">{{ selectedIcon }}</v-icon>
          </template>

          <template v-slot:item.action="{ item }">
            <v-btn icon @click="tripRequest(item.date_start)" v-if="travelStart !== item.date_start">
              <v-icon>
                {{ mapIcon }}
              </v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </v-expand-transition>
  </v-card>

</template>

<script>
import backend from '../../../backend'
import {
  mdiCameraOff,
  mdiCarBrakeAlert, mdiCellphone,
  mdiCheckboxMarkedCircle,
  mdiClipboardListOutline,
  mdiClockIn, mdiFood, mdiFormatListNumbered,
  mdiMapSearch, mdiSeatbelt,
  mdiSleep, mdiSmoking
} from '@mdi/js'
import Vuetify from '../../../plugins/vuetify'
import { travelDateLocalized } from '../../../utils/travels'

import { DRIVER_TABLE_MODULE, SETTINGS_MODULE } from '../../../store/modules'
import { createHelpers } from 'vuex-map-fields'
import snackbarMixin from '../../../mixins/snackbarMixin'
import showContentMixin from '../../../mixins/showContentMixin'
import { getCurrentTimezoneForBackend } from '@/utils/timezone'

const { mapFields } = createHelpers({
  getterType: `${DRIVER_TABLE_MODULE}/getField`,
  mutationType: `${DRIVER_TABLE_MODULE}/updateField`
})

const { mapFields: settingsFields } = createHelpers({
  getterType: `${SETTINGS_MODULE}/getField`
})

export default {
  name: 'tripScroll',
  props: {
    driverId: Number,
    travelStart: Number
  },
  data: () => ({
    expanded: [],
    // options: {
    //   sortBy: ['date_start'],
    //   sortDesc: [true]
    // },
    // totalTrips: 0,
    // trips: [],
    tableLoading: true,
    footerProps: { 'items-per-page-options': [5, 10] }, // if add more - page overflow can be reached!

    mapIcon: mdiMapSearch,
    barIcon: mdiFormatListNumbered,
    selectedIcon: mdiCheckboxMarkedCircle
  }),
  mixins: [snackbarMixin, showContentMixin(false)],
  computed: {
    ...mapFields(['drivers']),
    ...settingsFields(['mapSettings.hideSmallTrips', 'mapSettings.hideViewedTrips', 'mapLocalFilters.timeRangeFilter']),
    headers: () => ([
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.dateStart.title'),
        align: 'left',
        value: 'date_start',
        icon: mdiClockIn
      },
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.distractionCount.title'),
        align: 'left',
        value: 'distraction_count',
        icon: mdiCarBrakeAlert
      },
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.drowsinessCount.title'),
        align: 'left',
        value: 'drowsiness_count',
        icon: mdiSleep
      },
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.nobeltCount.title'),
        align: 'left',
        value: 'nobelt_count',
        icon: mdiSeatbelt
      },
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.cameraSabotage.title'),
        align: 'left',
        value: 'camera_sabotage',
        icon: mdiCameraOff
      },
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.cellphoneUse.title'),
        align: 'left',
        value: 'cellphone_use',
        icon: mdiCellphone
      },
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.eatingCount.title'),
        align: 'left',
        value: 'eating_count',
        icon: mdiFood
      },
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.smokingCount.title'),
        align: 'left',
        value: 'smoking_count',
        icon: mdiSmoking
      },
      {
        text: Vuetify.framework.lang.t('$vuetify.map.travelStats.actions.title'),
        value: 'action',
        sortable: false,
        width: '5%',
        icon: mdiClipboardListOutline
      }
    ]),
    iconedHeaders () {
      return this.headers.filter(header => header.hasOwnProperty('icon')).map(header => header.value)
    },
    driverData () {
      return this.drivers[this.driverId]
    }
  },
  methods: {
    tripRequest (travelStart) {
      this.$emit('trip-request', { userId: this.driverId, travelStart })
    },
    async loadTrips (tripDate = null) {
      if (this.driverId > 0 && this.driverData) {
        this.tableLoading = true
        try {
          const conditions = {}

          if (this.hideSmallTrips) {
            conditions.hideShortTrips = true
          }

          if (this.hideViewedTrips) {
            conditions.hideViewedTrips = true
          }

          if (this.timeRangeFilter.isUsed) {
            conditions.timeRangeFilter = {
              ...this.timeRangeFilter.filter,
              timezone: getCurrentTimezoneForBackend()
            }
          }

          // need
          let request = { driver_id: this.driverId, ...this.driverData.options, conditions }
          if (tripDate) {
            request = { ...request, trip_datestart: tripDate }
          }

          const result = await backend.fetchDriverTripList(request)
          if ('data' in result) {
            this.driverData.trips = result.data.items
            this.driverData.totalTrips = result.data.total
          }
        } catch (e) {
          this.processFailedRequest(e)
        } finally {
          this.tableLoading = false
        }
      } else {
        this.tableLoading = false
      }
    }
  },
  watch: {
    // async driverId (value) {
    //   if (value > 0) {
    //     await this.loadTrips()
    //   }
    // },
    'driverData.options': {
      async handler () {
        // we don't use mount for initial loading because first data loading occurs here
        await this.loadTrips()
      },
      deep: true
    }
  },
  created () {
    this['travelDateLocalized'] = travelDateLocalized
  }
}
</script>

<style scoped>
  >>> .tripScroll .v-data-table th {
    font-size: 12px;
  }

  >>> .tripScroll .v-data-table__progress [role='progressbar'] {
    position: absolute;
  }
</style>
